import { Volume2, VolumeX } from 'lucide-react'

import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Button } from '@/common/ui/button'
import WithdrawTable from '../WithdrawTable'
import { WithdrawTransactionResponse } from '../../types'
import { RefetchOptions } from '@tanstack/react-query'
import { CreateManualWithdrawDialog } from '@/common/ui/table/data-table-processor-selector/CreateManualWithdrawDialog'
import useHasPermission from '@/common/hooks/useHasPermission'
import { useWithdrawSoundPreference } from '@/common/hooks/useWithdrawSoundPreference'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { SortOrder } from '@/schema'

interface WithdrawListingProps {
	data: WithdrawTransactionResponse['data']
	total: number
	onRefresh: (options?: RefetchOptions) => Promise<unknown>
	isLoading?: boolean
	onSort?: (columnId: string, order: SortOrder | undefined) => void
}

export default function WithdrawListing({
	data,
	total,
	onRefresh,
	isLoading,
	onSort,
}: WithdrawListingProps) {
	const hasWritePermission = useHasPermission(
		PermissionKeys.MANAGE_BANK_ACCOUNT,
	)
	const { soundEnabled, toggleSound } = useWithdrawSoundPreference()

	return (
		<div className="space-y-4 px-4 sm:px-0">
			{/* Header Section - Mobile Responsive */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				{/* Title and Action Button */}
				<div className="space-y-3 sm:space-y-4">
					<div className="flex items-center gap-3">
						<Heading title="รายการถอนเงิน" description="รายการถอนเงินทั้งหมด" />
						<Button
							variant="ghost"
							size="icon"
							onClick={toggleSound}
							title={soundEnabled ? 'ปิดเสียงแจ้งเตือน' : 'เปิดเสียงแจ้งเตือน'}
							className={
								soundEnabled
									? 'shrink-0 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700'
									: 'shrink-0 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700'
							}
						>
							{soundEnabled ? (
								<Volume2 className="h-5 w-5" />
							) : (
								<VolumeX className="h-5 w-5" />
							)}
						</Button>
					</div>
					{hasWritePermission && <CreateManualWithdrawDialog />}
				</div>

				{/* Date Controls - Mobile Responsive */}
				<div className="flex flex-col gap-3 sm:items-end sm:gap-4">
					<DateRangePicker />
					<DateRangeButtons />
				</div>
			</div>

			<Separator className="my-4 sm:my-6" />

			{/* Table Section */}
			<WithdrawTable
				data={data}
				total={total}
				onRefresh={onRefresh}
				isLoading={isLoading}
				onSort={onSort}
			/>
		</div>
	)
}
